import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Mail, Phone, MapPin, Trash2, Check, X, Clock, Bell, FileEdit } from "lucide-react";
import { api } from "../../api";
import { Calendar } from "../ui/calendar";
import AppointmentCreateModal from "./AppointmentCreateModal";
import DevisCreateModal from "./DevisCreateModal";

const STATUS_META = {
  en_attente: { label: "En attente", cls: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30", dot: "bg-yellow-400" },
  confirme: { label: "Confirmé", cls: "bg-green-500/10 text-green-300 border-green-500/30", dot: "bg-green-400" },
  annule: { label: "Annulé", cls: "bg-white/5 text-white/50 border-white/10", dot: "bg-white/30" },
  termine: { label: "Terminé", cls: "bg-blue-500/10 text-blue-300 border-blue-500/30", dot: "bg-blue-400" },
};

function fmtLong(d) {
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
function toKey(d) {
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CalendarAgenda() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Date());
  const [createOpen, setCreateOpen] = useState(false);
  const [devisFor, setDevisFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/appointments"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); api.post("/admin/appointments/mark-seen").catch(() => {}); }, []);

  const byDay = useMemo(() => {
    const map = {};
    for (const a of items) {
      if (a.status === "annule") continue;
      (map[a.date] ||= []).push(a);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.time.localeCompare(b.time));
    return map;
  }, [items]);

  const daysWithEvents = useMemo(() => {
    return Object.keys(byDay).map((k) => {
      const [y, m, d] = k.split("-").map(Number); return new Date(y, m - 1, d);
    });
  }, [byDay]);

  const dayEvents = byDay[toKey(selected)] || [];

  const setStatus = async (id, status) => {
    await api.patch(`/admin/appointments/${id}`, { status });
    toast.success("Mis à jour"); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce rendez-vous ?")) return;
    await api.delete(`/admin/appointments/${id}`); toast.success("Supprimé"); load();
  };
  const sendReminder = async (id) => {
    try { await api.post(`/admin/appointments/${id}/reminder`); toast.success("Rappel envoyé au client"); load(); }
    catch { toast.error("Envoi impossible"); }
  };

  const upcoming = items.filter((i) => i.status !== "annule" && i.status !== "termine")
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  return (
    <div className="space-y-8" data-testid="calendar-agenda">
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="rounded-lg border border-white/10 bg-[#0f0f0f] p-4 lg:col-span-3">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-head text-lg font-medium uppercase tracking-tight text-white">Calendrier</h3>
            <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-4 py-2 text-xs font-semibold text-white hover:scale-[1.02] transition-transform" data-testid="agenda-add-btn"><Plus size={14} />Ajouter</button>
          </div>
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            modifiers={{ hasEvent: daysWithEvents }}
            modifiersClassNames={{ hasEvent: "!bg-[#e63946]/25 !text-white font-bold border border-[#e63946]/40" }}
            className="mx-auto text-white"
            data-testid="agenda-calendar"
          />
          <p className="mt-3 text-center text-[10px] uppercase tracking-wider text-white/40">Les jours avec rendez-vous sont en rouge</p>
        </div>

        <div className="rounded-lg border border-white/10 bg-[#0f0f0f] lg:col-span-2">
          <div className="border-b border-white/10 px-5 py-4">
            <p className="text-xs uppercase tracking-wider text-white/40">Journée du</p>
            <h3 className="mt-1 font-head text-lg font-medium uppercase tracking-tight text-white">{fmtLong(selected)}</h3>
            <p className="mt-1 text-xs text-white/50">{dayEvents.length} événement(s)</p>
          </div>
          {dayEvents.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm text-white/50">Aucun événement ce jour.</p>
              <button onClick={() => setCreateOpen(true)} className="mt-4 rounded-full border border-white/20 px-4 py-2 text-xs text-white hover:bg-white/5">+ Ajouter un événement</button>
            </div>
          ) : (
            <ul className="max-h-[420px] divide-y divide-white/5 overflow-y-auto">
              {dayEvents.map((a) => {
                const meta = STATUS_META[a.status] || STATUS_META.en_attente;
                return (
                  <li key={a.id} className="px-5 py-4" data-testid={`day-event-${a.id}`}>
                    <div className="flex items-start gap-3">
                      <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${meta.dot}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="flex items-center gap-1 font-medium text-white"><Clock size={12} className="text-[#ff4d4d]" />{a.time}</p>
                          <span className={`shrink-0 rounded border px-2 py-0.5 text-[9px] uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>
                        </div>
                        <p className="mt-1 text-sm text-white">{a.name} <span className="text-white/50">— {a.category}</span></p>
                        {a.address && <p className="mt-0.5 flex items-center gap-1 text-xs text-white/50"><MapPin size={10} />{a.address}</p>}
                        <div className="mt-3 flex flex-wrap gap-1">
                          {a.status !== "confirme" && <button onClick={() => setStatus(a.id, "confirme")} className="rounded border border-green-500/30 px-2 py-0.5 text-[10px] text-green-300 hover:bg-green-500/10"><Check size={10} className="mr-1 inline" />Confirmer</button>}
                          {a.status !== "termine" && <button onClick={() => setStatus(a.id, "termine")} className="rounded border border-blue-500/30 px-2 py-0.5 text-[10px] text-blue-300 hover:bg-blue-500/10">Terminé</button>}
                          <button onClick={() => sendReminder(a.id)} className="rounded border border-white/10 px-2 py-0.5 text-[10px] text-white/70 hover:bg-white/5"><Bell size={10} className="mr-1 inline" />Rappel</button>
                          <button onClick={() => setDevisFor(a)} className="rounded bg-brand-gradient px-2 py-0.5 text-[10px] font-semibold text-white"><FileEdit size={10} className="mr-1 inline" />Devis</button>
                          <button onClick={() => setStatus(a.id, "annule")} className="rounded border border-white/10 px-2 py-0.5 text-[10px] text-white/60 hover:bg-white/5"><X size={10} /></button>
                          <button onClick={() => remove(a.id)} className="rounded border border-red-500/30 px-2 py-0.5 text-[10px] text-red-400 hover:bg-red-500/10"><Trash2 size={10} /></button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Full list */}
      <div className="rounded-lg border border-white/10 bg-[#0f0f0f]">
        <div className="border-b border-white/10 px-5 py-4">
          <h3 className="font-head text-lg font-medium uppercase tracking-tight text-white">Tous les rendez-vous</h3>
          <p className="text-xs text-white/50">{upcoming.length} à venir</p>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-white/50">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-white/50">Aucun rendez-vous.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {items.map((a) => {
              const meta = STATUS_META[a.status] || STATUS_META.en_attente;
              return (
                <li key={a.id} className="grid gap-3 px-5 py-4 md:grid-cols-12" data-testid={`all-appt-${a.id}`}>
                  <div className="md:col-span-3">
                    <p className="text-xs uppercase tracking-wider text-white/40">Créneau</p>
                    <p className="mt-1 flex items-center gap-2 text-sm font-medium text-white"><Clock size={12} className="text-[#ff4d4d]" />{a.date} · {a.time}</p>
                  </div>
                  <div className="md:col-span-3">
                    <p className="text-xs uppercase tracking-wider text-white/40">Client</p>
                    <p className="mt-1 text-sm font-medium text-white">{a.name}</p>
                    <p className="text-xs text-white/50">{a.category}</p>
                  </div>
                  <div className="md:col-span-3">
                    <a href={`mailto:${a.email}`} className="flex items-center gap-2 text-xs text-white/70 hover:text-white"><Mail size={11} className="text-[#ff4d4d]" />{a.email}</a>
                    {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-2 text-xs text-white/70 hover:text-white"><Phone size={11} className="text-[#ff4d4d]" />{a.phone}</a>}
                  </div>
                  <div className="flex flex-col items-start gap-2 md:col-span-3">
                    <span className={`rounded border px-2 py-0.5 text-[10px] uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>
                    <div className="flex flex-wrap gap-1">
                      <button onClick={() => sendReminder(a.id)} className="rounded border border-white/10 px-2 py-0.5 text-[10px] text-white/70 hover:bg-white/5" title="Rappel email"><Bell size={10} /></button>
                      <button onClick={() => setDevisFor(a)} className="rounded bg-brand-gradient px-2 py-0.5 text-[10px] font-semibold text-white">Devis</button>
                      <button onClick={() => remove(a.id)} className="rounded border border-red-500/30 px-2 py-0.5 text-[10px] text-red-400 hover:bg-red-500/10"><Trash2 size={10} /></button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {createOpen && (
        <AppointmentCreateModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          initialDate={selected}
          onCreated={load}
        />
      )}
      {devisFor && (
        <DevisCreateModal
          open={!!devisFor}
          onOpenChange={(v) => !v && setDevisFor(null)}
          prefill={{
            client_name: devisFor.name, client_email: devisFor.email,
            client_phone: devisFor.phone, client_address: devisFor.address,
            project: devisFor.notes || devisFor.category, appointment_id: devisFor.id,
          }}
          onCreated={() => { setDevisFor(null); toast.success("Devis créé"); }}
        />
      )}
    </div>
  );
}
